import { useCallback, useEffect, useMemo } from 'react';
import { Checkbox, FormControlLabel, Grid2 as Grid, Typography } from '@mui/material';
import { globalAction } from 'common/actions';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { BoxedUrlWithCopy } from 'components';
import { GridSkeleton } from 'components/Grid';
import { SCIMTokenProvider, useLayoutContext } from 'context';
import { useSCIMConfiguration } from 'pages/Firms/hooks';
import { useReadUserAccessLevel } from 'services/hooks';
import { GroupMapping, SCIMTokenManagement } from './components';
import { ResponsiveFormGroup, ResponsivePaper } from './ScimConfiguration.styles';

const SCIMConfiguration = () => {
  const [{ firmInfo, user }, dispatch] = useStore() as unknown as UseStoreValues;
  const { updatePageActions } = useLayoutContext();

  // see to encapsulate this logic in a custom hook to be used both in SingleSignOn and SCIMConfiguration pages
  const firmId = useMemo(() => firmInfo?.id ?? null, [firmInfo]);

  const { isAdminOrStaffOrSuperuser } = useReadUserAccessLevel({ user, firmInfo });

  const {
    scimConfiguration,
    fetchSCIMConfiguration,
    updateSCIMConfiguration,
    isEnabled,
    groupMappings,
    setGroupMappings,
    setIsEnabled,
    tokens,
    newlyCreatedToken,
    setNewlyCreatedToken,
    generateToken,
    isLoading,
    rotateToken,
    revokeToken,
  } = useSCIMConfiguration({ firmId });

  const saveConfiguration = useCallback(async () => {
    if (firmId && scimConfiguration) {
      dispatch(globalAction.showLoadingProgress(true));
      await updateSCIMConfiguration(scimConfiguration.id!, {
        enabled: isEnabled,
        group_mappings: groupMappings,
        firm: firmId,
      });
      await fetchSCIMConfiguration(firmId);
      dispatch(globalAction.showLoadingProgress(false));
    }
  }, [firmId, scimConfiguration, isEnabled, groupMappings, updateSCIMConfiguration, fetchSCIMConfiguration, dispatch]);

  useEffect(() => {
    if (firmId && updatePageActions && isAdminOrStaffOrSuperuser) {
      updatePageActions({
        mainAction: {
          id: 'firm-scim-config-save',
          label: 'Save',
          isActive: false,
          callback: saveConfiguration,
        },
      });
    }
  }, [firmId, updatePageActions, saveConfiguration, isAdminOrStaffOrSuperuser]);

  if (scimConfiguration === null) {
    return <GridSkeleton />;
  }

  return (
    <Grid container spacing={4} sx={{ mb: 10, flexDirection: { xs: 'column', lg: 'row' } }}>
      <Grid size={{ xs: 12, lg: 6 }}>
        <Typography variant="h6" gutterBottom>
          SCIM Configuration
        </Typography>
        <ResponsiveFormGroup sx={{ mt: 3 }}>
          <Typography variant="h4" gutterBottom>
            SCIM endpoints base
          </Typography>
          <BoxedUrlWithCopy url={scimConfiguration.scim_base_url!} />
          <Typography variant="h4" gutterBottom sx={{ mt: 2 }}>
            Enable SCIM
          </Typography>
          <Typography variant="body2">
            Allows you to automate user provisioning and deprovisioning based on your Identity Provider groups.
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={isEnabled}
                onChange={() => setIsEnabled(prev => !prev)}
                name="scimEnabled"
                disabled={!isAdminOrStaffOrSuperuser}
              />
            }
            label="Enabled"
          />
        </ResponsiveFormGroup>
        <ResponsiveFormGroup>
          <GroupMapping groupMappings={groupMappings} setGroupMappings={setGroupMappings} />
        </ResponsiveFormGroup>
      </Grid>
      <Grid size={{ xs: 12, lg: 6 }} sx={{ mt: { xs: 2, lg: '1.25em' } }}>
        <ResponsivePaper elevation={4}>
          <SCIMTokenProvider
            tokens={tokens}
            newlyCreatedToken={newlyCreatedToken}
            generateToken={generateToken}
            isLoading={isLoading}
            setNewlyCreatedToken={setNewlyCreatedToken}
            rotateToken={rotateToken}
            revokeToken={revokeToken}>
            <SCIMTokenManagement />
          </SCIMTokenProvider>
        </ResponsivePaper>
      </Grid>
    </Grid>
  );
};
export default SCIMConfiguration;
